import React, { useEffect, useRef, useState } from 'react';
import { introBridge } from '../state/introBridge';
import { SLICE_DISTRICT } from '../config';

// IntroTitles — the cinematic titling over the street fly-through: a "<LOCATION>
// <time>" card, then "Welcome", plus a Skip button. Driven by introBridge (t =
// fly-through progress 0..1) via rAF and direct style writes — no per-frame React
// re-render. Invisible until the fly-through is active.
function ramp(t, a, b, c, d) {
  // 0 → fade up (a..b) → 1 → hold → fade down (c..d) → 0
  if (t <= a || t >= d) return 0;
  if (t < b) return (t - a) / (b - a);
  if (t > c) return 1 - (t - c) / (d - c);
  return 1;
}

export default function IntroTitles() {
  const wrap = useRef();
  const title = useRef();
  const welcome = useRef();
  const [time] = useState(() => {
    try { return new Intl.DateTimeFormat('en-US', { timeZone: SLICE_DISTRICT.timezone, hour: 'numeric', minute: '2-digit' }).format(new Date()); }
    catch { return ''; }
  });

  useEffect(() => {
    let raf;
    const tick = () => {
      const b = introBridge;
      if (wrap.current) {
        wrap.current.style.opacity = b.active ? '1' : '0';
        wrap.current.style.pointerEvents = b.active ? 'auto' : 'none';
      }
      if (title.current) title.current.style.opacity = String(ramp(b.t, 0.04, 0.12, 0.36, 0.46));
      if (welcome.current) welcome.current.style.opacity = String(ramp(b.t, 0.54, 0.62, 0.82, 0.93));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={wrap}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, opacity: 0, pointerEvents: 'none',
        transition: 'opacity 0.4s ease', fontFamily: 'system-ui, sans-serif',
        // Subtle top+bottom vignette so titles read over the bright street.
        background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 26%, rgba(0,0,0,0) 74%, rgba(0,0,0,0.4) 100%)',
      }}
    >
      <div ref={title} style={{ position: 'absolute', top: '15%', left: 0, right: 0, textAlign: 'center', opacity: 0 }}>
        <div style={{ fontSize: 'clamp(30px, 6.2vw, 60px)', fontWeight: 800, letterSpacing: 3, color: '#fff', textShadow: '0 4px 32px rgba(0,0,0,0.75)' }}>
          {(SLICE_DISTRICT.name || 'The World').toUpperCase()}
        </div>
        {time && (
          <div style={{ marginTop: 10, fontSize: 'clamp(13px, 2.4vw, 18px)', letterSpacing: 5, color: '#ffd27d', textShadow: '0 2px 20px rgba(0,0,0,0.7)' }}>
            {time}
          </div>
        )}
      </div>

      <div ref={welcome} style={{ position: 'absolute', top: '44%', left: 0, right: 0, textAlign: 'center', opacity: 0, fontSize: 'clamp(24px, 4.6vw, 48px)', fontWeight: 300, letterSpacing: 5, color: '#ffe9c4', textShadow: '0 4px 32px rgba(0,0,0,0.75)', textWrap: 'balance', padding: '0 16px' }}>
        {SLICE_DISTRICT.name ? `Welcome to ${SLICE_DISTRICT.name}` : 'Welcome'}
      </div>

      <button
        onClick={() => { introBridge.skip = true; }}
        style={{
          // Above the player bar (components/PlayerBar publishes its height) —
          // it used to sit beside the old Music toggle, and landed on the bar.
          position: 'absolute', right: 'max(16px, env(safe-area-inset-right))', bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))',
          pointerEvents: 'auto', cursor: 'pointer', padding: '9px 18px', borderRadius: 999,
          font: '600 13px system-ui', letterSpacing: 0.6, color: '#ffe9c4',
          background: 'rgba(16,10,6,0.55)', border: '1px solid rgba(255,255,255,0.28)',
          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        Skip ▸
      </button>
    </div>
  );
}
